import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CompleteUser, GetUsersResponse, UserFilters, WalletUpdateRequest, Transaction, Invite } from '../../types';
import apiService from '../../services/api';

interface UsersState {
  users: CompleteUser[];
  selectedUser: CompleteUser | null;
  userTransactions: Transaction[];
  userInvites: Invite[];
  transactionPagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  } | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statistics: {
    totalUsers: number;
    activeUsers: number;
    totalWalletBalance: number;
  };
  filters: UserFilters;
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  users: [],
  selectedUser: null,
  userTransactions: [],
  userInvites: [],
  transactionPagination: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  statistics: {
    totalUsers: 0,
    activeUsers: 0,
    totalWalletBalance: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: UserFilters }) => {
    const response = await apiService.getUsers(page, limit, filters);
    return response;
  }
);

export const fetchUserDetails = createAsyncThunk(
  'users/fetchUserDetails',
  async (userId: string) => {
    const response = await apiService.getUserDetails(userId);
    return response;
  }
);

export const updateUserWallet = createAsyncThunk(
  'users/updateUserWallet',
  async (data: WalletUpdateRequest) => {
    const response = await apiService.updateUserWallet(data);
    return response;
  }
);

export const blockUser = createAsyncThunk(
  'users/blockUser',
  async ({ userId, reason }: { userId: string; reason?: string }) => {
    const response = await apiService.blockUser(userId, reason);
    return response;
  }
);

export const unblockUser = createAsyncThunk(
  'users/unblockUser',
  async ({ userId, reason }: { userId: string; reason?: string }) => {
    const response = await apiService.unblockUser(userId, reason);
    return response;
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userId: string) => {
    const response = await apiService.deleteUser(userId);
    return response;
  }
);

export const resetUserPassword = createAsyncThunk(
  'users/resetPassword',
  async (userId: string) => {
    const response = await apiService.resetUserPassword(userId);
    return response;
  }
);

export const getUserTransactions = createAsyncThunk(
  'users/getUserTransactions',
  async ({ userId, page = 1, limit = 10 }: { userId: string; page?: number; limit?: number }) => {
    const response = await apiService.getUserTransactions(userId, page, limit);
    return { ...response, userId };
  }
);

export const getUserInvites = createAsyncThunk(
  'users/getUserInvites',
  async (userId: string) => {
    const response = await apiService.getUserInvites(userId);
    return { ...response, userId };
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedUser: (state, action: PayloadAction<CompleteUser | null>) => {
      state.selectedUser = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setFilters: (state, action: PayloadAction<UserFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
        // Direct access to the response data since it's not wrapped in ApiResponse
        state.users = action.payload.users;
        state.pagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          total: action.payload.pagination.total,
          totalPages: action.payload.pagination.pages,
        };
        state.statistics = action.payload.statistics;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        // Direct access to the user data since it's not wrapped in ApiResponse
        state.selectedUser = action.payload;
      })
      .addCase(updateUserWallet.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        const index = state.users.findIndex(user => user.id === updatedUser.id);
        if (index !== -1) {
          state.users[index] = updatedUser;
        }
        if (state.selectedUser?.id === updatedUser.id) {
          state.selectedUser = updatedUser;
        }
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        const index = state.users.findIndex(user => user.id === updatedUser.id);
        if (index !== -1) {
          state.users[index] = updatedUser;
        }
      })
      .addCase(unblockUser.fulfilled, (state, action) => {
        const updatedUser = action.payload;
        const index = state.users.findIndex(user => user.id === updatedUser.id);
        if (index !== -1) {
          state.users[index] = updatedUser;
        }
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          // Remove user from the list
          state.users = state.users.filter(user => user.id !== action.meta.arg);
          if (state.selectedUser?.id === action.meta.arg) {
            state.selectedUser = null;
          }
        }
      })
      .addCase(getUserTransactions.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.userTransactions = action.payload.data;
          state.transactionPagination = action.payload.pagination;
        }
      })
      .addCase(getUserInvites.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.userInvites = action.payload.invites;
        }
      });
  },
});

export const { clearError, setSelectedUser, setPage, setFilters, clearFilters } = usersSlice.actions;
export default usersSlice.reducer; 